// Tableau de bord (maire, secrétaire général, administration) : registre du jour, tuiles pilotées
// par le catalogue, filtres de période et d'arrondissement, disposition personnalisable.
import { Bandeau, Bouton, Filtres, Onglets, useFiltresPersistes, type ValeursFiltres } from '@cc/ui';
import { Eye, RotateCcw, Settings2 } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { usePreferences } from '../../preferences/FournisseurPreferences';
import { useSession } from '../../session/FournisseurSession';
import { rolesActifs } from '../../session/permissions';
import { LIBELLES_TABLEAUX, tableauxDisponibles, tuilesVisibles, type IdTableau } from './catalogue';
import { useModulesActifs, type FiltresTableau } from './donnees';
import { calculerPeriode, type CodePeriode } from './periode';
import { RegistreDuJour, SemaineEcheances } from './Registres';
import { SelecteurArrondissement } from './SelecteurArrondissement';
import { Tuile } from './Tuile';
import { TuileCarte } from './TuileCarte';
import styles from './tableau.module.css';

function Tableau({ id, filtres }: { id: IdTableau; filtres: FiltresTableau }) {
  const { contexte } = useSession();
  const { disposition, enregistrerDisposition, mode, erreur } = usePreferences();
  const modulesActifs = useModulesActifs();
  const [edition, setEdition] = useState(false);
  const courante = disposition(id);
  const { visibles, masquees } = tuilesVisibles(contexte, id, courante);
  const ordreComplet = () => [...visibles, ...masquees].map((t) => t.code);

  const deplacer = (code: string, sens: -1 | 1) => {
    const ordre = visibles.map((t) => t.code);
    const i = ordre.indexOf(code);
    const j = i + sens;
    if (i < 0 || j < 0 || j >= ordre.length) return;
    [ordre[i], ordre[j]] = [ordre[j] as string, ordre[i] as string];
    void enregistrerDisposition(id, { ordre: [...ordre, ...masquees.map((t) => t.code)], masquees: masquees.map((t) => t.code) });
  };
  const masquer = (code: string) =>
    void enregistrerDisposition(id, { ordre: ordreComplet(), masquees: [...masquees.map((t) => t.code), code] });
  const afficher = (code: string) =>
    void enregistrerDisposition(id, { ordre: ordreComplet(), masquees: masquees.map((t) => t.code).filter((c) => c !== code) });

  return (
    <div className={styles.cote}>
      <div className={styles.barre}>
        <h2>{LIBELLES_TABLEAUX[id]}</h2>
        <div className={styles.masquees}>
          <Bouton petit variante="secondaire" icone={Settings2} aria-pressed={edition} onClick={() => setEdition((e) => !e)}>
            {edition ? 'Terminer la personnalisation' : 'Personnaliser'}
          </Bouton>
          {courante ? (
            <Bouton petit variante="discret" icone={RotateCcw} onClick={() => void enregistrerDisposition(id, null)}>
              Rétablir la disposition par défaut
            </Bouton>
          ) : null}
        </div>
      </div>
      {edition && mode === 'appareil' ? <Bandeau type="info">La disposition est enregistrée sur cet appareil (enregistrement serveur non disponible).</Bandeau> : null}
      {erreur ? <Bandeau type="erreur">Disposition non enregistrée : {erreur}</Bandeau> : null}
      {visibles.length === 0 ? <p>Toutes les tuiles de ce tableau sont masquées ou hors de vos droits.</p> : null}
      <ul className={styles.tuiles} aria-label={`Tuiles : ${LIBELLES_TABLEAUX[id]}`}>
        {visibles.map((t, i) => (
          <li key={t.code}>
            <Tuile
              definition={t}
              filtres={filtres}
              modulesActifs={modulesActifs}
              edition={
                edition
                  ? {
                      onMonter: i > 0 ? () => deplacer(t.code, -1) : undefined,
                      onDescendre: i < visibles.length - 1 ? () => deplacer(t.code, 1) : undefined,
                      onMasquer: () => masquer(t.code),
                    }
                  : undefined
              }
            />
          </li>
        ))}
      </ul>
      {masquees.length > 0 ? (
        <div className={styles.masquees}>
          <span className="cc-etiquette">Tuiles masquées</span>
          {masquees.map((t) => (
            <Bouton key={t.code} petit variante="secondaire" icone={Eye} onClick={() => afficher(t.code)}>
              Afficher {t.libelle}
            </Bouton>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function PageTableauDeBord() {
  const { contexte } = useSession();
  const tableaux = tableauxDisponibles(rolesActifs(contexte));
  const [valeurs, setValeurs] = useFiltresPersistes('tableau-de-bord', { periode: 'mois', arrondissement: '' });
  const filtres: FiltresTableau = { periode: (valeurs.periode as CodePeriode) ?? 'mois', arrondissement: valeurs.arrondissement ?? '' };
  const periode = calculerPeriode(filtres.periode);
  const aujourdhui = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const maire = tableaux.includes('maire') || tableaux.includes('secretaire_general');

  return (
    <>
      <EnTetePage etiquette="Pilotage" titre={aujourdhui.charAt(0).toUpperCase() + aujourdhui.slice(1)} description={`Période : ${periode.libelle}, du ${periode.debut} au ${periode.fin}.`} />
      <div className={styles.barre}>
        <Filtres
          libelle="Filtres du tableau de bord"
          groupes={[
            {
              id: 'periode',
              libelle: 'Période',
              options: [
                { valeur: 'mois', libelle: 'Ce mois' },
                { valeur: 'trimestre', libelle: 'Trimestre' },
                { valeur: 'exercice', libelle: 'Exercice' },
              ],
            },
          ]}
          valeurs={valeurs}
          onChanger={(v: ValeursFiltres) => setValeurs({ ...valeurs, ...v })}
        />
        <SelecteurArrondissement valeur={filtres.arrondissement} onChanger={(a) => setValeurs({ ...valeurs, arrondissement: a })} />
      </div>
      {maire ? (
        <div className={styles.grille}>
          <RegistreDuJour />
          <SemaineEcheances />
        </div>
      ) : null}
      {maire ? <TuileCarte arrondissement={filtres.arrondissement} /> : null}
      {tableaux.length > 1 ? (
        <Onglets
          libelle="Tableaux de bord"
          onglets={tableaux.map((t) => ({ id: t, libelle: LIBELLES_TABLEAUX[t], contenu: <Tableau id={t} filtres={filtres} /> }))}
        />
      ) : tableaux[0] ? (
        <Tableau id={tableaux[0]} filtres={filtres} />
      ) : null}
    </>
  );
}
